import React, { useEffect, useState, useCallback } from 'react';
import { EmployeeDesignation, ReleaseAnnouncementDraft, ReleaseFeature, UserRole } from '../../../types';
import SettingsSectionShell from '../SettingsSectionShell';
import { hasReleaseAudienceSelection } from '../../../utils/releaseAudience';
import {
  subscribeToPendingReleaseDraft,
  draftToPreviewRelease,
  publishReleaseDraft,
  skipReleaseDraft,
} from '../../../utils/releaseAnnouncements';
import ReleaseAnnouncementPopup from '../../release/ReleaseAnnouncementPopup';
import ReleaseAudiencePicker from '../../release/ReleaseAudiencePicker';
import '../../release/ReleaseAnnouncementPopup.css';
import './ReleaseNotesSettingsSection.css';

const SESSION_KEY_PREFIX = 'lctf:releaseDraftLater:';

const ReleaseNotesSettingsSection: React.FC = () => {
  const [draft, setDraft] = useState<ReturnType<typeof draftToPreviewRelease> | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [rawDraft, setRawDraft] = useState<ReleaseAnnouncementDraft | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audienceRoles, setAudienceRoles] = useState<UserRole[]>([]);
  const [audienceDesignations, setAudienceDesignations] = useState<EmployeeDesignation[]>([]);
  const [audienceUserIds, setAudienceUserIds] = useState<string[]>([]);
  const [features, setFeatures] = useState<ReleaseFeature[]>([]);
  const [title, setTitle] = useState('');

  useEffect(() => {
    return subscribeToPendingReleaseDraft(pending => {
      setRawDraft(pending);
      if (pending) {
        setDraftId(pending.id);
        setDraft(draftToPreviewRelease(pending));
        setAudienceRoles(pending.audienceRoles);
        setAudienceDesignations(pending.audienceDesignations ?? []);
        setAudienceUserIds(pending.audienceUserIds ?? []);
        setFeatures(pending.features);
        setTitle(pending.title);
      } else {
        setDraftId(null);
        setDraft(null);
        setShowPreview(false);
        setAudienceRoles([]);
        setAudienceDesignations([]);
        setAudienceUserIds([]);
        setFeatures([]);
        setTitle('');
      }
    });
  }, []);

  const handlePublish = useCallback(async () => {
    if (!rawDraft) return;
    setPublishing(true);
    setError(null);
    setMessage(null);
    try {
      await publishReleaseDraft(rawDraft, { title, audienceRoles, audienceDesignations, audienceUserIds, features });
      setShowPreview(false);
      const audienceSummary = [
        ...audienceRoles,
        ...audienceDesignations.map((d) => (d === 'executive_assistant' ? 'EAs' : d)),
        ...(audienceUserIds.length
          ? [`${audienceUserIds.length} ${audienceUserIds.length === 1 ? 'person' : 'people'}`]
          : []),
      ];
      setMessage(
        `Release v${rawDraft.version} published to ${audienceSummary.join(', ')}.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Publish failed.');
    } finally {
      setPublishing(false);
    }
  }, [rawDraft, title, audienceRoles, audienceDesignations, audienceUserIds, features]);

  const handleSkip = useCallback(async () => {
    if (!draftId) return;
    setPublishing(true);
    setError(null);
    try {
      await skipReleaseDraft(draftId);
      setShowPreview(false);
      setMessage('Release draft skipped.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not skip draft.');
    } finally {
      setPublishing(false);
    }
  }, [draftId]);

  const openPreview = () => {
    try {
      if (draftId) sessionStorage.removeItem(`${SESSION_KEY_PREFIX}${draftId}`);
    } catch {
      /* ignore */
    }
    setShowPreview(true);
  };

  return (
    <SettingsSectionShell
      title="Release notes"
      description="Review and publish auto-generated release notes after each deploy."
    >
      {message && <p className="release-notes-message release-notes-message--ok">{message}</p>}
      {error && <p className="release-notes-message release-notes-message--err">{error}</p>}

      {!draft ? (
        <div className="release-notes-empty">
          <p>No pending release draft.</p>
          <p className="release-notes-hint">
            After you deploy to production, CI saves auto-generated release notes here for you to
            review and publish.
          </p>
        </div>
      ) : (
        <div className="release-notes-pending">
          <div className="release-notes-pending-header">
            <span className="release-notes-version">v{draft.version}</span>
            <h3>{title || draft.title}</h3>
          </div>
          <p className="release-notes-meta">
            {draft.features.length} feature{draft.features.length === 1 ? '' : 's'}
          </p>
          <ReleaseAudiencePicker
            selected={audienceRoles}
            selectedDesignations={audienceDesignations}
            selectedUserIds={audienceUserIds}
            onChange={setAudienceRoles}
            onDesignationsChange={setAudienceDesignations}
            onUserIdsChange={setAudienceUserIds}
            disabled={publishing}
          />
          <ul className="release-notes-feature-list">
            {draft.features.map((f, i) => (
              <li key={`${f.title}-${i}`}>
                <strong>{f.title}</strong>
                {f.area && <span className="release-notes-area">{f.area}</span>}
              </li>
            ))}
          </ul>
          <div className="release-notes-actions">
            <button type="button" className="pm-btn pm-btn-secondary" onClick={openPreview}>
              Preview popup
            </button>
            <button
              type="button"
              className="pm-btn pm-btn-secondary"
              onClick={handleSkip}
              disabled={publishing}
            >
              Skip
            </button>
            <button
              type="button"
              className="pm-btn pm-btn-primary"
              onClick={handlePublish}
              disabled={publishing || !hasReleaseAudienceSelection(audienceRoles, audienceDesignations, audienceUserIds)}
            >
              {publishing ? 'Publishing…' : 'Publish release notes'}
            </button>
          </div>
        </div>
      )}

      {showPreview && draft && (
        <ReleaseAnnouncementPopup
          release={{ ...draft, title: title || draft.title, features, audienceRoles, audienceDesignations, audienceUserIds }}
          mode="admin"
          publishing={publishing}
          audienceRoles={audienceRoles}
          audienceDesignations={audienceDesignations}
          audienceUserIds={audienceUserIds}
          onAudienceChange={setAudienceRoles}
          onAudienceDesignationsChange={setAudienceDesignations}
          onAudienceUserIdsChange={setAudienceUserIds}
          features={features}
          onFeaturesChange={setFeatures}
          onTitleChange={setTitle}
          onDismiss={() => setShowPreview(false)}
          onPublish={async () => {
            await handlePublish();
          }}
          onSkip={async () => {
            await handleSkip();
          }}
        />
      )}
    </SettingsSectionShell>
  );
};

export default ReleaseNotesSettingsSection;
